import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { StudyCoughEventType } from "../../../misc/api/apiTypes";
import { Authorization } from "../../../misc/components/Authorization";
import * as Actions from "../actions";
import { AnalyseState, CoughOverreadFixedComment } from "../interfaces/state";
import { AnalysisSetsButton } from "./AnalysisSetsButton";
import { DownloadButton } from "./DownloadButton";
import { EventListButton } from "./EventListButton";
import { DisputedListButton } from "./DisputedListButton";
import { FixedCommentsButton } from "./FixedCommentsButton";
import EventActionDashboard from "./EventActionDashboard";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { OnIgnoreEnter } from "../AnalyseApp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { CoughAnalysisSetTypeEnum } from "../../../misc/api/CoughAnalysisSetTypes";
import AutoQCButton from "./AutoQCButton";

export interface ActionDashboardProps {
    studyId: string;
    sessionId: string;
    selectedWaveform: string;
    availableEventTypes: StudyCoughEventType[];
    selectedFixedCommentsCount: number;
    selectedAnalysisSetsCount: number;
    dispatch: Dispatch<AnalyseState>;
    configErrorMessage: string;
    sessionNotesCount: number;
    acceptanceErrorMessage: string;
    disputedAcceptanceErrorMessage: string;
    selectedSegment: number;
    isSegmented: boolean;
    adjudicationEnabled: boolean;
    isQCStatementsOpen: boolean;
}

export const ActionDashboard = ({ studyId, sessionId, selectedWaveform, selectedFixedCommentsCount, selectedAnalysisSetsCount, configErrorMessage, dispatch, sessionNotesCount, acceptanceErrorMessage, disputedAcceptanceErrorMessage, selectedSegment, isSegmented, adjudicationEnabled, isQCStatementsOpen }: ActionDashboardProps) => {
    const { currentUserPermissions } = useSecurityContext();

    const getHourSegmentsListBtn = (isLoadedCorrectly: boolean) => {
        if (isLoadedCorrectly) {
            return (
                <button
                    id="hourSegmentsListBtn"
                    type="button"
                    className="btn btn-primary"
                    onClick={() => dispatch(Actions.OpenHourSegmentsModalAction.create())}
                    onKeyDown={OnIgnoreEnter}
                >
                    Hour Segments List
                </button>
            );
        } else {
            return null;
        }
    };

    return (
        <div className="row">
            <EventActionDashboard />
            <div className="col-lg-7 text-right">
                {adjudicationEnabled === true ? <> <DisputedListButton
                    acceptanceErrorMessageVisible={disputedAcceptanceErrorMessage !== null}
                    onClick={() =>
                        dispatch(Actions.OpenDisputedListDialogAction.create())
                    }
                />&nbsp;</> : null}
                {getHourSegmentsListBtn(configErrorMessage === null)}
                &nbsp;
                <EventListButton
                    acceptanceErrorMessageVisible={acceptanceErrorMessage !== null}
                    onClick={() =>
                        dispatch(Actions.OpenEventListDialogAction.create())
                    }
                />&nbsp;
                <Authorization
                    permissionIdsAny={[PermissionEnum.ViewAllCoughSessionNotes, PermissionEnum.AddCoughSessionNotes]}
                    currentPermissions={currentUserPermissions}
                    studyId={studyId}
                >
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            dispatch(
                                Actions.ToggleSessionNotesVisibilityAction.create()
                            )
                        }
                        onKeyDown={OnIgnoreEnter}
                    >
                        <FontAwesomeIcon icon={faComments} /> Session Notes
                        &nbsp;<span className="badge badge-info">{sessionNotesCount}</span>
                    </button>&nbsp;
                </Authorization>
                <AnalysisSetsButton
                    activeAnalysisSetsCount={selectedAnalysisSetsCount}
                    onClick={() =>
                        dispatch(Actions.OpenAnalysisSetDialogAction.create())
                    }
                />&nbsp;
                <FixedCommentsButton
                    selectedCommentsCount={selectedFixedCommentsCount}
                    onClick={() =>
                        dispatch(Actions.OpenFixedCommentsDialogAction.create())
                    }
                />&nbsp;
                <Authorization
                    permissionId={PermissionEnum.DownloadCoughRecordings}
                    currentPermissions={currentUserPermissions}
                    studyId={studyId}
                >
                    <DownloadButton
                        sessionId={sessionId}
                        waveformType={selectedWaveform}
                        split={selectedSegment}
                        isSplit={isSegmented}
                    />
                </Authorization>&nbsp;
                <AutoQCButton sessionId={sessionId} />
            </div>
        </div>
    );
}

function mapStateToProps(state: AnalyseState) {
    let selectedFixedComments: CoughOverreadFixedComment[] = [];
    if (state.analysisSet.coughAnalysisSets.length > 0) {
        // Selecting the fixed comments that are selected in the primary analysis set.
        const primaryAnalysisSetId = state.analysisSet.primaryAnalystSetId;
        const primaryAnalysisSet = state.analysisSet.coughAnalysisSets.find(set => {
            return set.Id === primaryAnalysisSetId;
        });

        selectedFixedComments = state.core.availableFixedComments.filter(com => {
            return primaryAnalysisSet.FixedCommentIds.includes(com.id);
        });
    }

    return {
        studyId: state.core.studyId,
        sessionId: state.core.sessionMetadata.sessionId,
        availableEventTypes: state.core.availableEventTypes,
        selectedWaveform: state.core.selectedWaveform,
        isSegmented: state.core.isSegmented,
        selectedFixedCommentsCount: selectedFixedComments.length,
        selectedAnalysisSetsCount:
            state.analysisSet.coughAnalysisSets.length > 0
                ? state.analysisSet.coughAnalysisSets.filter(x => x.IsSelected).length
                : null,
        configErrorMessage: state.core.configErrorMessage,
        sessionNotesCount: state.core.sessionNotes.length,
        acceptanceErrorMessage: state.core.acceptanceErrorMessage,
        disputedAcceptanceErrorMessage: state.core.disputedAcceptanceErrorMessage,
        selectedSegment: state.core.selectedSegment,
        adjudicationEnabled: state.analysisSet.coughAnalysisSets.length > 0
            ? state.core.adjudicationEnabled ? state.analysisSet.coughAnalysisSets.filter(x => x.IsSelected && x.Type.Id === CoughAnalysisSetTypeEnum.Adjudication).length > 0
            : false : false,
        isQCStatementsOpen: state.core.isQCStatementsOpen,
    } as ActionDashboardProps;
}

export default connect<ActionDashboardProps, any>(mapStateToProps)(ActionDashboard);
